// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import typography from "@bg/XDL/PreStyle/typography";
import translate from "@insite/client-framework/Translate";
import Tooltip from "@insite/mobius/Tooltip";
import Typography from "@insite/mobius/Typography";
import { FontWeightProperty } from "csstype";
import React from "react";

export const dataTable = [
    {
        date: "1/5/2018",
        order: 1234504,
        shipTo: "MFG Co., 123 North Fifth, Minneapolis, MN 55406",
        status: "Submitted",
        po: 10000,
        total: 805.92,
    },
    {
        date: "1/4/2018",
        order: 1234503,
        shipTo: "MFG Co., 123 North Fifth, Minneapolis, MN 55406",
        status: "Shipped",
        po: 10000,
        total: 44.01,
    },
    {
        date: "1/3/2018",
        order: 1234502,
        shipTo: "MFG Co., 123 North Fifth, Minneapolis, MN 55406",
        status: "RMA",
        po: 10000,
        total: 123.45,
    },
    {
        date: "1/2/2018",
        order: 1234501,
        shipTo: "MFG Co., 123 North Fifth, Minneapolis, MN 55406",
        status: "Complete",
        po: 10000,
        total: 44.01,
    },
    {
        date: "1/1/2018",
        order: 1234500,
        shipTo: "MFG Co., 123 North Fifth, Minneapolis, MN 55406",
        status: "Complete",
        po: 10000,
        total: 123.45,
    },
];

export const longText =
    "To obtain a price quote, add this item to your cart and select 'Request a Quote' from the cart page. You can request a quote for multiple items.";

export const dynamicDropdownOptions = [
    {
        optionText: "apple apple apple apple apple apple apple apple apple apple apple apple apple apple ",
        searchString: "rouge reed redd read",
    },
    { optionText: "orange", searchString: "orag", disabled: true },
    { optionText: "orangered", searchString: "orag" },
    { optionText: "yellow", searchString: "yelloe yeallow", disabled: true },
    { optionText: "green", searchString: "gren grreen grren" },
    { optionText: "blue" },
    { optionText: "indigo", searchString: "jean" },
    { optionText: "violet", searchString: "purple" },
];

export const dummyLinks = [
    {
        children: "Home",
        href: "https://home.insitesoft.com",
    },
    {
        children: "Category",
        href: "https://subcategory.insitesoft.com",
    },
    {
        children: "Subcategory-1",
        href: "https://subcategory.insitesoft.com",
    },
    {
        children: "Subcategory-2",
        href: "https://subcategory.insitesoft.com",
    },
    {
        children: "Subcategory-3",
        href: "https://subcategory.insitesoft.com",
    },
    {
        children: "Subcategory-4",
        href: "https://subcategory.insitesoft.com",
    },
    {
        children: "Subcategory-5",
        href: "https://subcategory.insitesoft.com",
    },
    {
        children: "Current Page",
    },
];

export const dynamicDropdownConfigs: any = [
    { label: "Dynamic Dropdown" },
    { label: "Dynamic Dropdown", disabled: true },
    { label: "Dynamic Dropdown", sizeVariant: "small" },
    { label: "Dynamic Dropdown", disabled: true, sizeVariant: "small" },
    {
        label: (
            <>
                {"Dynamic Dropdown "}
                <Tooltip text="A selection of theme values that will be accepted" />
            </>
        ),
    },
];

export const selectOptions = [
    { value: "Day", label: translate("Day") },
    { value: "Month", label: translate("Month") },
];

export const selectConfigs = [
    { label: "Select the best one", required: true, placeholder: "Select" },
    { label: "Select the best one", disabled: true, placeholder: "Select" },
    {
        label: "Pagination",
        required: true,
        placeholder: "Pagination",
        options: [
            { value: 8, label: "8" },
            { value: 16, label: "16" },
            { value: 32, label: "32" },
        ],
    },
    { label: "Select the best one", placeholder: "Select", sizeVariant: "small" },
    { label: "Select the best one", disabled: true, placeholder: "Select", sizeVariant: "small" },
    {
        label: (
            <>
                {"Select the best one "}
                <Tooltip text="A selection of theme values that will be accepted" />
            </>
        ),
    },
];

const parseClampMaxValue = (clampValue: string) => {
    if (!clampValue.toString().startsWith("clamp(")) {
        return clampValue; // Return as is if not a clamp
    }

    // Extract values within clamp()
    const values = clampValue.match(/clamp\(([^)]+)\)/)?.[1].split(",");
    const maxValue = values?.[2].trim(); // Third value is the max
    return maxValue;
};

const tabletStyles: any = {
    h1: { size: 38, lineHeight: "44px" },
    h2: { size: 32, lineHeight: "38px" },
    h3: { size: 30, lineHeight: "35px" },
};

const mobileStyles: any = {
    h1: { size: 38, lineHeight: "44px" },
    h2: { size: 32, lineHeight: "38px" },
    h3: { size: 30, lineHeight: "35px" },
};

const formatText = ({
    key,
    weight,
    size,
    lineHeight,
}: {
    key: any;
    weight: FontWeightProperty;
    size: string;
    lineHeight: string;
}) => {
    return (
        <Typography variant={key} size={size} weight={weight} lineHeight={lineHeight}>
            {`[Font] : [${weight}]`} <br /> {`Font-Size [${parseClampMaxValue(size)}]`}
            <br /> {`Line-Height [${parseClampMaxValue(lineHeight)}]`}
        </Typography>
    );
};

export const headingTexts = Object.keys(typography)
    .filter(key => ["h1", "h2", "h3", "h4", "h5", "h6"].includes(key)) // Only process h1-h6
    .reduce((result: any, key) => {
        const desktopText = formatText({
            key,
            weight: (typography as any)[key].weight,
            size: (typography as any)[key].size,
            lineHeight: (typography as any)[key].lineHeight,
        });

        result[key] = {
            desktop: desktopText,

            tablet: tabletStyles[key]
                ? formatText({
                      key,
                      weight: (typography as any)[key].weight,
                      size: tabletStyles[key].size,
                      lineHeight: tabletStyles[key].lineHeight,
                  })
                : desktopText, // Default to desktop if no tablet style

            mobile: mobileStyles[key]
                ? formatText({
                      key,
                      weight: (typography as any)[key].weight,
                      size: mobileStyles[key].size,
                      lineHeight: mobileStyles[key].lineHeight,
                  })
                : desktopText, // Default to desktop if no mobile style
        };

        return result;
    }, {});
export const colorsConfig = {
    Brand: [
        { text: "", colorDisplay: "brand-01", color: "primary.main" },
        { text: "", colorDisplay: "brand-02", color: "secondary.main" },
    ],
    "Body Copy": [{ text: "", colorDisplay: "text-01", color: "text.main" }],
    Headlines: [
        { text: "h1", colorDisplay: "ui-07", color: "common.ui7" },
        { text: "h2", colorDisplay: "ui-07", color: "common.ui7" },
        { text: "h3", colorDisplay: "ui-07", color: "common.ui7" },
        { text: "h4", colorDisplay: "ui-07", color: "common.ui7" },
        { text: "h5", colorDisplay: "ui-07", color: "common.ui7" },
        { text: "h6", colorDisplay: "ui-07", color: "common.ui7" },
    ],
    "Primary Action (Buttons, Links)": [
        { text: "Inactive State", colorDisplay: "ui-08", color: "common.ui8" },
        { text: "Hover State", colorDisplay: "ui-09", color: "common.ui9" },
        { text: "Focus State", colorDisplay: "ui-10", color: "common.ui10" },
    ],
    "Secondary Action (Forms)": [
        { text: "Inactive State", colorDisplay: "ui-04", color: "common.ui4" },
        { text: "Hover State", colorDisplay: "ui-09", color: "common.ui9" },
        { text: "Focus State", colorDisplay: "ui-10", color: "common.ui10" },
    ],
    "Tertiary Action": [
        { text: "Inactive State", colorDisplay: "ui-08", color: "common.ui8" },
        { text: "Hover State", colorDisplay: "ui-09", color: "common.ui9" },
        { text: "Focus State", colorDisplay: "ui-10", color: "common.ui10" },
    ],
    Grayscale: [
        { text: "UI : Mono : 1 : White", colorDisplay: "ui-01", color: "common.ui1", addBorder: true },
        { text: "UI : Mono : 2", colorDisplay: "ui-02", color: "common.ui2" },
        { text: "UI : Mono : 3", colorDisplay: "ui-03", color: "common.ui3" },
        { text: "UI : Mono : 4", colorDisplay: "ui-04", color: "common.ui4" },
        { text: "UI : Mono : 5", colorDisplay: "ui-05", color: "common.ui5" },
        { text: "UI : Mono : 6", colorDisplay: "ui-06", color: "common.ui6" },
        { text: "UI : Mono : 7 : Black", colorDisplay: "ui-07", color: "common.ui7" },
    ],
    "Site Colors": [
        { text: "Site : Color", colorDisplay: "ui-08", color: "text.accent" },
        { text: "Site : Color", colorDisplay: "ui-08", color: "text.accent" },
        { text: "Site : Color", colorDisplay: "ui-08", color: "text.accent" },
        { text: "Site : Color", colorDisplay: "ui-08", color: "text.accent" },
        { text: "Site : Color", colorDisplay: "ui-08", color: "text.accent" },
        { text: "Site : Color", colorDisplay: "ui-08", color: "text.accent" },
    ],
    "Support Status": [
        { text: "Support : Success", colorDisplay: "support-01", color: "success.main" },
        { text: "Support : Danger", colorDisplay: "support-02", color: "danger.main" },
        { text: "Support : Warning", colorDisplay: "support-03", color: "warning.main" },
        { text: "Support : Info", colorDisplay: "support-04", color: "info.main" },
    ],
};
